import { useState, useEffect, useRef } from "react";
import emailjs from '@emailjs/browser';

const ink = "#1d2a35";
const paper = "#f4f1ea";
const blue = "#2b5f8a";
const red = "#c9401f";
const mono = "'IBM Plex Mono', 'Courier New', monospace";
const grotesk = "'Space Grotesk', 'Helvetica Neue', sans-serif";

const services = [
  {
    title: "General Maintenance & Repairs",
    desc: "Light repair work, fixture fixes, and day-to-day upkeep to keep your property in prime condition.",
    tag: "MAINTENANCE",
  },
  {
    title: "Roofing Repairs",
    desc: "Leak fixes, shingle repairs, and general roof maintenance to protect your property year-round.",
    tag: "ROOFING",
  },
  {
    title: "Renovations",
    desc: "Small to mid-size renovation projects — from single rooms to full property refreshes, handled start to finish.",
    tag: "RENOVATION",
  },
  {
    title: "Interior & Exterior Painting",
    desc: "Walls, trim, doors, fences, and full repaints — clean lines and durable finishes that make a property feel brand new.",
    tag: "PAINTING",
  },
  {
    title: "Cleaning Services",
    desc: "Move-in/move-out cleans, post-construction deep cleans, and scheduled regular cleaning packages.",
    tag: "CLEANING",
  },
  {
    title: "Carpet & Upholstery Cleaning",
    desc: "Deep steam cleaning for carpets, sofas, and upholstered furniture — lifting stains, dirt, and odours.",
    tag: "CARPET",
  },
  {
    title: "Hot Tub Cleaning",
    desc: "Draining, deep cleaning, and maintenance to keep hot tubs sanitary and guest-ready.",
    tag: "HOT TUB",
  },
  {
    title: "Move-In Inspections",
    desc: "Observational cleanliness and condition checklists so tenants step into a property that's truly ready.",
    tag: "INSPECTION",
  },
  {
    title: "Airbnb Design & Setup",
    desc: "We transform properties into guest-ready short-term rentals — styling, staging, and full setup included.",
    tag: "AIRBNB",
  },
  {
    title: "Yard Cleanup & Snow Removal",
    desc: "Seasonal yard maintenance, basic snow clearing, and waste disposal to keep exteriors pristine.",
    tag: "EXTERIOR",
  },
  {
    title: "Gutter Cleaning",
    desc: "Clearing leaves and debris to keep gutters and downspouts flowing and protect your roof and foundation.",
    tag: "GUTTERS",
  },
  {
    title: "Junk & Debris Removal",
    desc: "Haul-away service for unwanted furniture, renovation debris, and general junk — fast and hassle-free.",
    tag: "REMOVAL",
  },
  {
    title: "Property Manager Support",
    desc: "We collaborate alongside licensed property managers, handling operational tasks so nothing falls through the cracks.",
    tag: "SUPPORT",
  },
  {
    title: "Property Listing Photography",
    desc: "Professional-quality photos taken to showcase your property at its best — ideal for rental listings, Airbnb, and marketing.",
    tag: "MEDIA",
  },
  {
    title: "Fire Safety Plan Preparation",
    desc: "We prepare clear, compliant fire escape and safety plans for residential and commercial properties to meet building requirements.",
    tag: "SAFETY",
  },
  {
    title: "Furniture Assembly",
    desc: "From flat-pack to fully assembled — we put together furniture of all kinds so your space is move-in ready from day one.",
    tag: "SETUP",
  },
];

const galleryCategories = [
  { key: "all", label: "All Work" },
  { key: "airbnb", label: "Airbnb Setup" },
  { key: "painting", label: "Painting" },
  { key: "carpet", label: "Carpet & Upholstery" },
  { key: "roofing", label: "Roofing" },
  { key: "gutters", label: "Gutter Cleaning" },
  { key: "hottub", label: "Hot Tub Cleaning" },
];

const galleryBase = process.env.PUBLIC_URL + "/gallery";

const galleryImages = [
  ...Array.from({ length: 11 }, (_, i) => ({
    src: `${galleryBase}/airbnb-setup/${i + 1}.jpeg`,
    category: "airbnb",
    tag: "AIRBNB SETUP",
  })),
  ...Array.from({ length: 8 }, (_, i) => ({
    src: `${galleryBase}/painting/${i + 1}.jpg`,
    category: "painting",
    tag: "PAINTING",
  })),
  ...Array.from({ length: 10 }, (_, i) => ({
    src: `${galleryBase}/carpet-and-upholstery-cleaning/${i + 1}.jpeg`,
    category: "carpet",
    tag: "CARPET & UPHOLSTERY",
  })),
  ...Array.from({ length: 2 }, (_, i) => ({
    src: `${galleryBase}/roofing/${i + 1}.jpeg`,
    category: "roofing",
    tag: "ROOFING",
  })),
  ...Array.from({ length: 3 }, (_, i) => ({
    src: `${galleryBase}/gutter-cleaning/${i + 1}.jpeg`,
    category: "gutters",
    tag: "GUTTER CLEANING",
  })),
  ...Array.from({ length: 2 }, (_, i) => ({
    src: `${galleryBase}/hot-tub-before/${i + 1}.jpeg`,
    category: "hottub",
    tag: "HOT TUB — BEFORE",
  })),
  ...Array.from({ length: 2 }, (_, i) => ({
    src: `${galleryBase}/hot-tub-after/${i + 1}.jpeg`,
    category: "hottub",
    tag: "HOT TUB — AFTER",
  })),
];

const hotTubPairs = [
  { before: `${galleryBase}/hot-tub-before/1.jpeg`, after: `${galleryBase}/hot-tub-after/1.jpeg` },
  { before: `${galleryBase}/hot-tub-before/2.jpeg`, after: `${galleryBase}/hot-tub-after/2.jpeg` },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function SectionHeader({ sheet, label, title }) {
  return (
    <div style={{ marginBottom: "3rem" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.2rem",
      }}>
        <span style={{
          fontFamily: mono, fontSize: "0.7rem", letterSpacing: "0.15em",
          color: red, whiteSpace: "nowrap",
        }}>{sheet} / {label}</span>
        <span style={{ flex: 1, height: "1px", background: ink, opacity: 0.25 }} />
        <span style={{ fontFamily: mono, fontSize: "0.7rem", color: ink, opacity: 0.4 }}>MP-{new Date().getFullYear()}</span>
      </div>
      <h2 style={{
        fontFamily: grotesk, fontWeight: 700,
        fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
        color: ink, lineHeight: 1.05, letterSpacing: "-0.02em",
      }}>{title}</h2>
    </div>
  );
}

function SpecRow({ num, title, desc, tag, delay }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className="spec-row"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms, background 0.2s ease`,
        borderBottom: `1px dashed rgba(29,42,53,0.3)`,
        padding: "1.6rem 0.8rem",
      }}
    >
      <span className="spec-num" style={{
        fontFamily: mono, fontSize: "0.85rem", fontWeight: 600,
        color: blue, transition: "color 0.2s",
      }}>{String(num).padStart(2, "0")}</span>
      <div>
        <h3 style={{
          fontFamily: grotesk, fontSize: "1.25rem", fontWeight: 700,
          color: ink, marginBottom: "0.4rem", letterSpacing: "-0.01em",
        }}>{title}</h3>
        <p style={{ color: "#5d6a75", fontSize: "0.92rem", lineHeight: 1.7, maxWidth: "560px" }}>{desc}</p>
      </div>
      <span className="spec-tag" style={{
        fontFamily: mono, fontSize: "0.65rem", letterSpacing: "0.12em",
        color: ink, border: `1px solid rgba(29,42,53,0.35)`,
        padding: "0.3rem 0.6rem", height: "fit-content",
        justifySelf: "end", whiteSpace: "nowrap",
      }}>{tag}</span>
    </div>
  );
}

function CropMarks() {
  const mark = (pos) => ({
    position: "absolute", width: "18px", height: "18px", ...pos,
  });
  const line = `2px solid ${ink}`;
  return (
    <>
      <div style={{ ...mark({ top: "1.2rem", left: "1.2rem" }), borderTop: line, borderLeft: line }} />
      <div style={{ ...mark({ top: "1.2rem", right: "1.2rem" }), borderTop: line, borderRight: line }} />
      <div style={{ ...mark({ bottom: "1.2rem", left: "1.2rem" }), borderBottom: line, borderLeft: line }} />
      <div style={{ ...mark({ bottom: "1.2rem", right: "1.2rem" }), borderBottom: line, borderRight: line }} />
    </>
  );
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("");
  const [galleryFilter, setGalleryFilter] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Close menu on resize to desktop
  useEffect(() => {
    const fn = () => { if (window.innerWidth > 768) setMenuOpen(false); };
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleSubmit = () => {
    setStatus("sending");
    emailjs.send(
      "service_1e8dn83",
      "template_maty8ch",
      {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        message: formData.message,
        to_email: "info@muhaxhiri.ca",
      },
      "a_08en0KppYL7W33Y"
    ).then(() => {
      setStatus("sent");
      setFormData({ name: "", email: "", phone: "", message: "" });
    }).catch(() => {
      setStatus("error");
    });
  };

  const [heroRef, heroIn] = useInView(0.05);
  const [galleryRef, galleryIn] = useInView();
  const [aboutRef, aboutIn] = useInView();
  const [ctaRef, ctaIn] = useInView();

  const filteredGallery = galleryFilter === "all"
    ? galleryImages
    : galleryImages.filter(g => g.category === galleryFilter);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = () => setLightboxIndex(i => (i + 1) % filteredGallery.length);
  const prevImage = () => setLightboxIndex(i => (i - 1 + filteredGallery.length) % filteredGallery.length);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const fn = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") setLightboxIndex(i => (i + 1) % filteredGallery.length);
      if (e.key === "ArrowLeft") setLightboxIndex(i => (i - 1 + filteredGallery.length) % filteredGallery.length);
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [lightboxIndex, filteredGallery.length]);

  const gridBg = {
    backgroundImage: `
      linear-gradient(rgba(43,95,138,0.07) 1px, transparent 1px),
      linear-gradient(90deg, rgba(43,95,138,0.07) 1px, transparent 1px),
      linear-gradient(rgba(43,95,138,0.13) 1px, transparent 1px),
      linear-gradient(90deg, rgba(43,95,138,0.13) 1px, transparent 1px)`,
    backgroundSize: "20px 20px, 20px 20px, 100px 100px, 100px 100px",
  };

  const navItems = [["01", "services"], ["02", "gallery"], ["03", "about"], ["04", "contact"]];

  return (
    <div style={{ fontFamily: grotesk, background: paper, color: ink, minHeight: "100vh", overflowX: "hidden" }}>

      <style>{`
        ::selection { background: ${red}; color: ${paper}; }
        @keyframes slideDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }

        .desktop-nav { display: flex; gap: 1.8rem; align-items: center; }
        .hamburger { display: none; }
        .mobile-menu { display: none; }

        .nav-link {
          background: none; border: none; cursor: pointer;
          font-family: ${mono}; font-size: 0.72rem; letter-spacing: 0.12em;
          text-transform: uppercase; color: ${ink}; transition: color 0.2s;
        }
        .nav-link:hover { color: ${red}; }
        .nav-link .num { color: ${blue}; margin-right: 0.35rem; }

        .btn-ink {
          display: inline-block; padding: 0.9rem 2.2rem; background: ${ink}; color: ${paper};
          border: 2px solid ${ink}; cursor: pointer; text-decoration: none; text-align: center;
          font-family: ${mono}; font-size: 0.75rem; letter-spacing: 0.12em;
          text-transform: uppercase; font-weight: 600; transition: all 0.2s;
        }
        .btn-ink:hover { background: ${red}; border-color: ${red}; }

        .btn-outline {
          display: inline-block; padding: 0.9rem 2.2rem; background: transparent; color: ${ink};
          border: 2px solid ${ink}; cursor: pointer; text-decoration: none; text-align: center;
          font-family: ${mono}; font-size: 0.75rem; letter-spacing: 0.12em;
          text-transform: uppercase; font-weight: 600; transition: all 0.2s;
        }
        .btn-outline:hover { color: ${red}; border-color: ${red}; }

        .btn-paper {
          display: inline-block; padding: 0.9rem 2.2rem; background: ${paper}; color: ${ink};
          border: 2px solid ${paper}; cursor: pointer; text-decoration: none; text-align: center;
          font-family: ${mono}; font-size: 0.75rem; letter-spacing: 0.12em;
          text-transform: uppercase; font-weight: 600; transition: all 0.2s;
        }
        .btn-paper:hover { background: ${red}; border-color: ${red}; color: ${paper}; }

        .btn-paper-outline {
          display: inline-block; padding: 0.9rem 2.2rem; background: transparent; color: ${paper};
          border: 2px solid rgba(244,241,234,0.5); cursor: pointer; text-decoration: none; text-align: center;
          font-family: ${mono}; font-size: 0.75rem; letter-spacing: 0.12em;
          text-transform: uppercase; font-weight: 600; transition: all 0.2s;
        }
        .btn-paper-outline:hover { border-color: ${paper}; }

        .spec-row {
          display: grid; grid-template-columns: 64px 1fr auto;
          gap: 1.2rem; align-items: start;
        }
        .spec-row:hover { background: rgba(43,95,138,0.06); }
        .spec-row:hover .spec-num { color: ${red}; }

        .gallery-thumb:hover img { transform: scale(1.06); }
        .gallery-thumb:hover .gallery-thumb-tag { opacity: 1; }

        .wo-input {
          width: 100%; padding: 0.9rem 1rem; background: #fdfcf9;
          border: 1.5px solid ${ink}; border-radius: 0; color: ${ink};
          font-family: ${grotesk}; font-size: 0.95rem; outline: none;
          box-sizing: border-box; transition: border-color 0.2s;
        }
        .wo-input:focus { border-color: ${red}; }
        .wo-input::placeholder { color: #9aa3ab; }

        .title-block { display: grid; grid-template-columns: 2fr 1.2fr 1fr 1fr; }
        .title-block > div { padding: 1.1rem 1.3rem; border-right: 1px solid ${ink}; }
        .title-block > div:last-child { border-right: none; }

        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: flex !important; }
          .mobile-menu { display: flex !important; }
          .hero-section { padding: 7rem 1.5rem 4rem !important; }
          .section-pad { padding: 4.5rem 1.5rem !important; }
          .about-grid { grid-template-columns: 1fr !important; gap: 2.5rem !important; }
          .stat-strip { flex-direction: column; }
          .stat-strip > div { border-right: none !important; border-bottom: 1px solid rgba(29,42,53,0.25); }
          .stat-strip > div:last-child { border-bottom: none; }
          .spec-row { grid-template-columns: 48px 1fr; }
          .spec-tag { display: none; }
          .title-block { grid-template-columns: 1fr; }
          .title-block > div { border-right: none !important; border-bottom: 1px solid ${ink}; }
          .title-block > div:last-child { border-bottom: none; }
          .contact-cols { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: "1rem 2rem",
        background: scrolled || menuOpen ? paper : "transparent",
        borderBottom: scrolled || menuOpen ? `2px solid ${ink}` : "2px solid transparent",
        transition: "all 0.3s ease",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        {/* Logo */}
        <div onClick={() => scrollTo("hero")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "0.8rem" }}>
          <div style={{
            width: "30px", height: "30px", border: `2px solid ${ink}`,
            overflow: "hidden", background: "#fff", flexShrink: 0,
          }}>
            <img
              src={process.env.PUBLIC_URL + "/muhaxhiri-logo.jpeg"}
              alt="Muhaxhiri Property logo"
              style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scale(1.45)" }}
            />
          </div>
          <div>
            <div style={{ fontFamily: grotesk, fontSize: "1.05rem", fontWeight: 700, color: ink, letterSpacing: "0.02em", lineHeight: 1.1 }}>
              MUHAXHIRI
            </div>
            <div style={{ fontFamily: mono, fontSize: "0.56rem", letterSpacing: "0.28em", color: blue, textTransform: "uppercase" }}>
              Property Services
            </div>
          </div>
        </div>

        {/* Desktop Nav */}
        <div className="desktop-nav">
          {navItems.map(([num, id]) => (
            <button key={id} className="nav-link" onClick={() => scrollTo(id)}>
              <span className="num">{num}/</span>{id}
            </button>
          ))}
          <button className="btn-ink" style={{ padding: "0.6rem 1.4rem" }} onClick={() => scrollTo("contact")}>
            Get a Quote
          </button>
        </div>

        {/* Hamburger Button */}
        <button
          className="hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: "none", // overridden by CSS media query
            flexDirection: "column", justifyContent: "center", alignItems: "center",
            gap: "5px", background: "none", border: "none", cursor: "pointer",
            padding: "4px", width: "36px", height: "36px",
          }}
          aria-label="Toggle menu"
        >
          {[0, 1, 2].map(i => (
            <span key={i} style={{
              display: "block", width: "22px", height: "2px", background: ink,
              transition: "all 0.3s ease",
              transform: menuOpen ? (i === 0 ? "rotate(45deg) translate(5px, 5px)" : i === 2 ? "rotate(-45deg) translate(5px, -5px)" : "none") : "none",
              opacity: menuOpen && i === 1 ? 0 : 1,
            }} />
          ))}
        </button>
      </nav>

      {/* Mobile Menu Dropdown */}
      {menuOpen && (
        <div
          className="mobile-menu"
          style={{
            position: "fixed", top: "68px", left: 0, right: 0, zIndex: 99,
            background: paper,
            borderBottom: `2px solid ${ink}`,
            flexDirection: "column", alignItems: "stretch",
            padding: "0.5rem 2rem 1.8rem",
            animation: "slideDown 0.25s ease",
          }}
        >
          {navItems.map(([num, id]) => (
            <button key={id} className="nav-link" onClick={() => scrollTo(id)} style={{
              borderBottom: "1px dashed rgba(29,42,53,0.25)",
              padding: "1.1rem 0", textAlign: "left", fontSize: "0.85rem",
            }}>
              <span className="num">{num}/</span>{id}
            </button>
          ))}
          <button className="btn-ink" style={{ marginTop: "1.2rem" }} onClick={() => scrollTo("contact")}>
            Get a Quote
          </button>
        </div>
      )}

      {/* HERO */}
      <section id="hero" className="hero-section" style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        justifyContent: "center", padding: "7rem 3rem 5rem",
        position: "relative", ...gridBg,
      }}>
        <CropMarks />

        <div ref={heroRef} style={{ position: "relative", zIndex: 1, maxWidth: "1100px", margin: "0 auto", width: "100%" }}>
          <div style={{
            fontFamily: mono, fontSize: "0.72rem", letterSpacing: "0.18em",
            textTransform: "uppercase", color: blue, marginBottom: "1.8rem",
            display: "flex", alignItems: "center", gap: "0.8rem",
            opacity: heroIn ? 1 : 0, transition: "opacity 0.8s ease 0.1s",
          }}>
            <span style={{ width: "10px", height: "10px", background: red, animation: "blink 2.5s infinite" }} />
            FIG. 1 — CALGARY, ALBERTA · RESIDENTIAL &amp; COMMERCIAL
          </div>

          <h1 style={{
            fontFamily: grotesk,
            fontSize: "clamp(2.8rem, 8vw, 6rem)",
            fontWeight: 700, lineHeight: 1.0, color: ink,
            letterSpacing: "-0.03em", marginBottom: "2rem",
            opacity: heroIn ? 1 : 0,
            transform: heroIn ? "translateY(0)" : "translateY(40px)",
            transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
          }}>
            Every property,<br />
            kept <span style={{
              color: red, position: "relative", display: "inline-block",
              borderBottom: `4px solid ${red}`,
            }}>to spec.</span>
          </h1>

          <p style={{
            fontSize: "1.05rem", lineHeight: 1.8, color: "#5d6a75", maxWidth: "560px",
            marginBottom: "2.5rem",
            opacity: heroIn ? 1 : 0,
            transform: heroIn ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s",
          }}>
            Maintenance, roofing, renovations, painting, and cleaning for homes, rentals, and commercial spaces across Calgary. We keep the details on schedule — so you don't have to.
          </p>

          <div style={{
            display: "flex", gap: "1rem", flexWrap: "wrap",
            opacity: heroIn ? 1 : 0,
            transform: heroIn ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.8s ease 0.5s, transform 0.8s ease 0.5s",
          }}>
            <button className="btn-ink" onClick={() => scrollTo("services")}>View the Spec Sheet</button>
            <a className="btn-outline" href="tel:5872226887">Tel: 587-222-6887</a>
          </div>

        </div>
      </section>

      {/* STAT STRIP */}
      <div className="stat-strip" style={{
        display: "flex", borderTop: `2px solid ${ink}`, borderBottom: `2px solid ${ink}`,
        background: paper,
      }}>
        {[
          ["SCOPE", "16 services under one roof"],
          ["SITES", "Residential · Commercial · Airbnb"],
          ["AREA", "Calgary + surrounding communities"],
        ].map(([label, value], i) => (
          <div key={label} style={{
            flex: 1, padding: "1.2rem 1.8rem",
            borderRight: i < 2 ? `1px solid rgba(29,42,53,0.25)` : "none",
          }}>
            <div style={{ fontFamily: mono, fontSize: "0.62rem", letterSpacing: "0.25em", color: red, marginBottom: "0.3rem" }}>{label}</div>
            <div style={{ fontSize: "0.92rem", fontWeight: 500, color: ink }}>{value}</div>
          </div>
        ))}
      </div>

      {/* SERVICES */}
      <section id="services" className="section-pad" style={{ padding: "6.5rem 3rem" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <SectionHeader sheet="SHEET 01" label="SCOPE OF WORK" title={<>The spec sheet.</>} />
          <p style={{ color: "#5d6a75", maxWidth: "540px", lineHeight: 1.8, marginBottom: "3rem", marginTop: "-1.5rem" }}>
            Sixteen trades, one phone number. Everything below is handled in-house by our own crew — no subcontractors to chase down.
          </p>

          <div style={{ borderTop: `2px solid ${ink}` }}>
            {services.map((s, i) => (
              <SpecRow key={s.title} num={i + 1} {...s} delay={(i % 4) * 70} />
            ))}
          </div>

          {/* Notice stamp */}
          <div style={{
            marginTop: "3.5rem", transform: "rotate(-0.6deg)",
            border: `2px solid ${red}`, padding: "0.5rem", maxWidth: "760px",
          }}>
            <div style={{ border: `1px solid ${red}`, padding: "1.3rem 1.6rem" }}>
              <div style={{ fontFamily: mono, fontSize: "0.68rem", letterSpacing: "0.3em", color: red, marginBottom: "0.6rem" }}>
                ⚠ NOTICE — READ BEFORE FILING
              </div>
              <p style={{ fontSize: "0.85rem", lineHeight: 1.8, color: ink }}>
                Muhaxhiri Property does not participate in real estate transactions, leasing, property management, or any activities requiring licensing under the Real Estate Act. All services are strictly operational and maintenance-based. We proudly support licensed property managers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="section-pad" style={{ padding: "6.5rem 3rem", borderTop: `2px solid ${ink}` }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <SectionHeader sheet="SHEET 02" label="FIELD PHOTOS" title={<>Recent work.</>} />
          <p style={{ color: "#5d6a75", maxWidth: "560px", lineHeight: 1.8, marginBottom: "2.5rem", marginTop: "-1.5rem" }}>
            Real jobs completed across Calgary — Airbnb setups, paint work, roofing, gutters, and hot tub cleaning. Tap any photo to view it larger.
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", marginBottom: "2.5rem" }}>
            {galleryCategories.map(c => (
              <button
                key={c.key}
                onClick={() => { setGalleryFilter(c.key); setLightboxIndex(null); }}
                style={{
                  fontFamily: mono, fontSize: "0.68rem", letterSpacing: "0.1em",
                  textTransform: "uppercase", padding: "0.55rem 1rem",
                  border: `1.5px solid ${ink}`, cursor: "pointer",
                  background: galleryFilter === c.key ? ink : "transparent",
                  color: galleryFilter === c.key ? paper : ink,
                  transition: "all 0.2s",
                }}
              >{c.label}</button>
            ))}
          </div>

          <div ref={galleryRef} style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "0.9rem",
            opacity: galleryIn ? 1 : 0,
            transform: galleryIn ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}>
            {filteredGallery.map((img, i) => {
              if (img.category === "hottub") return null;
              return (
                <div
                  key={img.src}
                  className="gallery-thumb"
                  onClick={() => openLightbox(i)}
                  style={{
                    position: "relative", aspectRatio: "1", overflow: "hidden",
                    border: `1.5px solid ${ink}`, cursor: "pointer", background: "#e8e4d9",
                  }}
                >
                  <img
                    src={img.src}
                    alt={img.tag}
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.4s ease" }}
                  />
                  <div className="gallery-thumb-tag" style={{
                    position: "absolute", left: 0, right: 0, bottom: 0,
                    padding: "0.5rem 0.6rem", background: "rgba(29,42,53,0.82)",
                    fontFamily: mono, fontSize: "0.6rem", letterSpacing: "0.1em",
                    color: paper, opacity: 0, transition: "opacity 0.2s ease",
                  }}>{img.tag}</div>
                </div>
              );
            })}
          </div>

          {(galleryFilter === "all" || galleryFilter === "hottub") && (
            <div style={{ marginTop: "2.5rem" }}>
              <div style={{
                fontFamily: mono, fontSize: "0.68rem", letterSpacing: "0.15em",
                color: red, marginBottom: "1rem", textTransform: "uppercase",
              }}>
                Hot Tub Cleaning — Before &amp; After
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {hotTubPairs.map((pair, idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", maxWidth: "360px" }}>
                    {[["BEFORE", pair.before, red], ["AFTER", pair.after, "#2e6b3a"]].map(([label, src, color]) => (
                      <div
                        key={label}
                        onClick={() => openLightbox(filteredGallery.findIndex(g => g.src === src))}
                        style={{
                          position: "relative", aspectRatio: "4 / 3", overflow: "hidden",
                          border: `1.5px solid ${ink}`, cursor: "pointer", background: "#e8e4d9",
                        }}
                      >
                        <img
                          src={src}
                          alt={`Hot tub cleaning — ${label.toLowerCase()}`}
                          loading="lazy"
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                        <span style={{
                          position: "absolute", top: "0.6rem", left: "0.6rem",
                          fontFamily: mono, fontSize: "0.62rem", letterSpacing: "0.12em",
                          padding: "0.3rem 0.65rem", fontWeight: 700,
                          background: color, color: paper,
                        }}>{label}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightboxIndex !== null && filteredGallery[lightboxIndex] && (
        <div
          onClick={closeLightbox}
          style={{
            position: "fixed", inset: 0, zIndex: 200,
            background: "rgba(29,42,53,0.94)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: "2rem",
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            aria-label="Close"
            style={{
              position: "absolute", top: "1.5rem", right: "1.5rem",
              background: "none", border: `1.5px solid ${paper}`, color: paper,
              width: "38px", height: "38px", cursor: "pointer", fontFamily: mono, fontSize: "1rem",
            }}
          >✕</button>

          {filteredGallery.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                aria-label="Previous"
                style={{
                  position: "absolute", left: "1.5rem", top: "50%", transform: "translateY(-50%)",
                  background: "none", border: `1.5px solid ${paper}`, color: paper,
                  width: "42px", height: "42px", cursor: "pointer", fontSize: "1.2rem",
                }}
              >‹</button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                aria-label="Next"
                style={{
                  position: "absolute", right: "1.5rem", top: "50%", transform: "translateY(-50%)",
                  background: "none", border: `1.5px solid ${paper}`, color: paper,
                  width: "42px", height: "42px", cursor: "pointer", fontSize: "1.2rem",
                }}
              >›</button>
            </>
          )}

          <img
            src={filteredGallery[lightboxIndex].src}
            alt={filteredGallery[lightboxIndex].tag}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "88vw", maxHeight: "76vh", objectFit: "contain", border: `2px solid ${paper}` }}
          />
          <div style={{
            marginTop: "1.2rem", fontFamily: mono, fontSize: "0.7rem",
            letterSpacing: "0.15em", color: paper, textTransform: "uppercase",
          }}>
            {filteredGallery[lightboxIndex].tag} — {lightboxIndex + 1} / {filteredGallery.length}
          </div>
        </div>
      )}

      {/* ABOUT */}
      <section id="about" className="section-pad" style={{ padding: "6.5rem 3rem", ...gridBg, borderTop: `2px solid ${ink}` }}>
        <div className="about-grid" style={{
          maxWidth: "1000px", margin: "0 auto",
          display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: "4rem", alignItems: "start",
        }}>

          {/* Left: title-block facts table */}
          <div style={{
            opacity: aboutIn ? 1 : 0,
            transform: aboutIn ? "translateX(0)" : "translateX(-30px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
            border: `2px solid ${ink}`, background: paper,
          }}>
            <div style={{
              padding: "1rem 1.3rem", borderBottom: `2px solid ${ink}`,
              fontFamily: mono, fontSize: "0.68rem", letterSpacing: "0.25em", color: paper,
              background: ink,
            }}>
              PROJECT DATA — MP/001
            </div>
            {[
              ["SITE", "Calgary, Alberta"],
              ["SCOPE", "Residential · Commercial · Airbnb"],
              ["TRADES", "Maintenance · Roofing · Renovations · Cleaning"],
              ["PARTNERS", "Licensed property managers"],
              ["STATUS", "Accepting new clients"],
            ].map(([label, value], i, arr) => (
              <div key={label} style={{
                display: "grid", gridTemplateColumns: "96px 1fr",
                borderBottom: i < arr.length - 1 ? `1px solid rgba(29,42,53,0.25)` : "none",
              }}>
                <div style={{
                  fontFamily: mono, fontSize: "0.62rem", letterSpacing: "0.15em",
                  color: blue, padding: "1rem 1.3rem",
                  borderRight: `1px solid rgba(29,42,53,0.25)`,
                }}>{label}</div>
                <div style={{ fontSize: "0.88rem", padding: "0.95rem 1.3rem", color: ink, fontWeight: 500 }}>
                  {label === "STATUS" ? <span style={{ color: red, fontWeight: 700 }}>{value}</span> : value}
                </div>
              </div>
            ))}
          </div>

          {/* Right: content */}
          <div ref={aboutRef} style={{
            opacity: aboutIn ? 1 : 0,
            transform: aboutIn ? "translateX(0)" : "translateX(30px)",
            transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
          }}>
            <SectionHeader sheet="SHEET 03" label="ABOUT US" title={<>The crew behind Calgary's best-kept properties.</>} />
            <p style={{ color: "#5d6a75", lineHeight: 1.9, marginBottom: "1.2rem", fontSize: "0.95rem", marginTop: "-1rem" }}>
              Based in Calgary, Muhaxhiri Property delivers reliable, detail-oriented maintenance, roofing, renovations, painting, and cleaning services for residential and commercial properties. We take pride in making every property spotless, functional, and ready.
            </p>
            <p style={{ color: "#5d6a75", lineHeight: 1.9, marginBottom: "2rem", fontSize: "0.95rem" }}>
              Whether you need a roof repair, a full renovation, a post-construction clean, a fresh coat of paint, an Airbnb redesign, routine upkeep, seasonal yard work, or junk removal — we handle it so you don't have to. We work alongside licensed property managers as a trusted operational partner.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
              {[
                "Licensed support without replacing your property manager",
                "In-house crew — no subcontractors, no chasing down vendors",
                "Trusted by Calgary property owners",
              ].map(item => (
                <div key={item} style={{ display: "flex", gap: "0.9rem", alignItems: "baseline" }}>
                  <span style={{ fontFamily: mono, color: red, fontSize: "0.8rem", fontWeight: 600 }}>[✓]</span>
                  <span style={{ color: ink, fontSize: "0.9rem", lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER — inverted ink panel */}
      <section className="section-pad" style={{
        padding: "5.5rem 3rem", background: ink, position: "relative",
        backgroundImage: `
          linear-gradient(rgba(244,241,234,0.045) 1px, transparent 1px),
          linear-gradient(90deg, rgba(244,241,234,0.045) 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
      }}>
        <div ref={ctaRef} style={{
          maxWidth: "1000px", margin: "0 auto",
          opacity: ctaIn ? 1 : 0,
          transform: ctaIn ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.8s ease, transform 0.8s ease",
        }}>
          <div style={{ fontFamily: mono, fontSize: "0.7rem", letterSpacing: "0.25em", color: "#e8a03f", marginBottom: "1.4rem" }}>
            SHEET 04 / NEXT STEPS
          </div>
          <h2 style={{
            fontFamily: grotesk, fontWeight: 700,
            fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
            color: paper, lineHeight: 1.08, letterSpacing: "-0.02em", marginBottom: "1.4rem",
          }}>
            Let's put your property<br />on the schedule.
          </h2>
          <p style={{ color: "rgba(244,241,234,0.55)", marginBottom: "2.5rem", lineHeight: 1.8, maxWidth: "480px" }}>
            Call or email us today for a free quote. We serve Calgary and surrounding areas.
          </p>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <a className="btn-paper" href="tel:5872226887">Tel: 587-222-6887</a>
            <a className="btn-paper-outline" href="mailto:info@muhaxhiri.ca">info@muhaxhiri.ca</a>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact" className="section-pad" style={{ padding: "6.5rem 3rem" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <SectionHeader sheet="SHEET 05" label="WORK ORDER" title={<>Request a free quote.</>} />

          <div style={{ border: `2px solid ${ink}`, padding: "2.2rem 2rem", background: paper, position: "relative" }}>
            <div style={{
              position: "absolute", top: "-12px", left: "1.5rem", background: paper,
              padding: "0 0.7rem", fontFamily: mono, fontSize: "0.65rem",
              letterSpacing: "0.25em", color: blue,
            }}>
              FORM MP-04 — WORK ORDER REQUEST
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "1.4rem" }}>
              {[
                { key: "name", label: "01 — FULL NAME", type: "text", placeholder: "Your name" },
                { key: "email", label: "02 — EMAIL ADDRESS", type: "email", placeholder: "your@email.com" },
                { key: "phone", label: "03 — PHONE NUMBER", type: "tel", placeholder: "403-xxx-xxxx" },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key}>
                  <label style={{
                    display: "block", fontFamily: mono, fontSize: "0.65rem",
                    letterSpacing: "0.18em", color: ink, marginBottom: "0.5rem",
                  }}>{label}</label>
                  <input
                    className="wo-input"
                    type={type}
                    placeholder={placeholder}
                    value={formData[key]}
                    onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                  />
                </div>
              ))}
              <div>
                <label style={{
                  display: "block", fontFamily: mono, fontSize: "0.65rem",
                  letterSpacing: "0.18em", color: ink, marginBottom: "0.5rem",
                }}>04 — DESCRIPTION OF WORK</label>
                <textarea
                  className="wo-input"
                  rows={5}
                  placeholder="Tell us about your property and what services you need..."
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  style={{ resize: "vertical" }}
                />
              </div>

              {status === "error" && (
                <p style={{ color: red, fontFamily: mono, fontSize: "0.78rem", textAlign: "center" }}>
                  ⚠ Transmission failed. Please try again or email us directly.
                </p>
              )}

              <button
                className="btn-ink"
                onClick={handleSubmit}
                disabled={status === "sending"}
                style={{
                  width: "100%",
                  background: status === "sent" ? "#2e6b3a" : undefined,
                  borderColor: status === "sent" ? "#2e6b3a" : undefined,
                  cursor: status === "sending" ? "not-allowed" : "pointer",
                  opacity: status === "sending" ? 0.7 : 1,
                }}
              >
                {status === "sending" ? "Transmitting..." : status === "sent" ? "✓ Received — we'll be in touch" : "Submit Work Order"}
              </button>
            </div>
          </div>

          <div className="contact-cols" style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1.5rem",
          }}>
            {[
              { label: "TEL", value: "587-222-6887", href: "tel:5872226887" },
              { label: "EMAIL", value: "info@muhaxhiri.ca", href: "mailto:info@muhaxhiri.ca" },
            ].map(({ label, value, href }) => (
              <a key={label} href={href} style={{
                border: `1.5px solid ${ink}`, padding: "1rem 1.3rem",
                textDecoration: "none", display: "flex", alignItems: "baseline", gap: "0.8rem",
              }}>
                <span style={{ fontFamily: mono, fontSize: "0.62rem", letterSpacing: "0.2em", color: red }}>{label}</span>
                <span style={{ color: ink, fontSize: "0.92rem", fontWeight: 600 }}>{value}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER — architectural title block */}
      <footer className="section-pad" style={{ padding: "0 3rem 3rem" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto", border: `2px solid ${ink}` }}>
          <div className="title-block">
            <div>
              <div style={{ fontFamily: mono, fontSize: "0.58rem", letterSpacing: "0.25em", color: blue, marginBottom: "0.4rem" }}>PROJECT</div>
              <div style={{ fontWeight: 700, fontSize: "0.95rem", color: ink }}>MUHAXHIRI PROPERTY INC.</div>
              <div style={{ fontSize: "0.75rem", color: "#5d6a75", marginTop: "0.2rem" }}>Property Maintenance, Renovations &amp; Cleaning Services</div>
            </div>
            <div>
              <div style={{ fontFamily: mono, fontSize: "0.58rem", letterSpacing: "0.25em", color: blue, marginBottom: "0.4rem" }}>CONTACT</div>
              <div style={{ fontSize: "0.78rem", color: ink, lineHeight: 1.7 }}>
                <a href="tel:5872226887" style={{ color: ink, textDecoration: "none" }}>587-222-6887</a><br />
                <a href="mailto:info@muhaxhiri.ca" style={{ color: ink, textDecoration: "none" }}>info@muhaxhiri.ca</a>
              </div>
            </div>
            <div>
              <div style={{ fontFamily: mono, fontSize: "0.58rem", letterSpacing: "0.25em", color: blue, marginBottom: "0.4rem" }}>LOCATION</div>
              <div style={{ fontSize: "0.78rem", color: ink, lineHeight: 1.7 }}>Calgary,<br />Alberta</div>
            </div>
            <div>
              <div style={{ fontFamily: mono, fontSize: "0.58rem", letterSpacing: "0.25em", color: blue, marginBottom: "0.4rem" }}>DATE</div>
              <div style={{ fontSize: "0.78rem", color: ink, lineHeight: 1.7 }}>© {new Date().getFullYear()}<br />All rights reserved</div>
            </div>
          </div>
          <div style={{
            borderTop: `1px solid ${ink}`, padding: "0.9rem 1.3rem",
            fontFamily: mono, fontSize: "0.62rem", letterSpacing: "0.08em", color: "#5d6a75", lineHeight: 1.7,
          }}>
            NOTES: Not a licensed real estate or property management company. All services operational &amp; maintenance-based.
          </div>
        </div>
      </footer>
    </div>
  );
}
